/**
 * PriorityBadge — etykieta pilności wniosku procesu (kolumna
 * `process_instances.priority`, patrz `DECISION_PRIORITIES` w `@saas/shared`).
 *
 * Reużywa prymityw `Badge` (`@/components/Badge`) — paleta `tone`:
 *  - `urgent` → danger (czerwony)
 *  - `high`   → warning (pomarańczowy/amber)
 *  - `normal` → neutral (szary)
 *  - `low`    → neutral + przyciemniona opacity (Badge nie ma osobnej
 *    „przygaszonej" tonacji — `low` dzieli paletę z `normal`, różni się
 *    tylko wizualnie niższą intensywnością).
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecisionPriority } from '@saas/shared';

import { Badge, type BadgeTone } from '@/components/Badge';

const PRIORITY_TONE: Record<DecisionPriority, BadgeTone> = {
  urgent: 'danger',
  high: 'warning',
  normal: 'neutral',
  low: 'neutral',
};

const PRIORITY_EXTRA_CLASS: Partial<Record<DecisionPriority, string>> = {
  low: 'opacity-60',
};

export interface PriorityBadgeProps {
  priority: DecisionPriority;
  testId?: string;
}

export function PriorityBadge({ priority, testId }: PriorityBadgeProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Badge
      tone={PRIORITY_TONE[priority]}
      size="sm"
      className={PRIORITY_EXTRA_CLASS[priority]}
      data-testid={testId}
    >
      {t(`tenant.processes.priorityLabel.${priority}`)}
    </Badge>
  );
}
